import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { Validators } from "@angular/forms";
import { FormText, SelectBoxOption, ViewMode, FormBuilder, FormGroup } from "@coso/forms";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { FilterType, FilterTypeName, GroupStorage, IExplicitFolderPermissionsDto, ISpecialPermissionHolder, PrincipalType } from "../../../../../models/auto-generated";
import { PrincipalTypeMap } from "../../models/PrincipalTypeMap";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";

@Component({
  standalone:false,
  templateUrl: "./add-edit-explicit-folder-permission.component.html"
})
export class AddEditExplicitFolderPermissionComponent implements OnInit, OnDestroy {
  private unsubscribe = new Subject<void>();
  @ViewChild("filterTypePopover", { static: true }) filterTypePopover: TemplateRef<any>;
  @ViewChild("filterValuePopover", { static: true }) filterValuePopover: TemplateRef<any>;
  @ViewChild("principalTypePopover", { static: true }) principalTypePopover: TemplateRef<any>;
  @ViewChild("principalValuePopover", { static: true }) principalValuePopover: TemplateRef<any>;

  ViewMode = ViewMode;
  GroupStorage = GroupStorage;
  FilterType = FilterType;
  PrincipalType = PrincipalType;

  errorMessage: string;
  viewMode: ViewMode;
  groupStorage: GroupStorage;
  information: IExplicitFolderPermissionsDto;
  entity: ISpecialPermissionHolder;
  saveCallback: (entity: ISpecialPermissionHolder) => void;

  formGroup: FormGroup<ISpecialPermissionHolder>;
  formText: FormText<ISpecialPermissionHolder>;

  private noneOption = { label: "---NONE---", value: null, disabled: true };

  private filterValueTxt: string;
  private filterValueOption: string;

  constructor(public activeModal: NgbActiveModal, private fb: FormBuilder) {}

  ngOnDestroy(): void {
    this.unsubscribe.next();
  }

  ngOnInit(): void {
    this.filterValueTxt = null;
    this.filterValueOption = this.information.entities[0].logicalName;
    this.formGroup = this.fb.group<ISpecialPermissionHolder>({
      filterType: this.fb.control(FilterType.Global, [Validators.required]),
      filterValue: this.fb.control(null),
      principalType: this.fb.control(this.groupStorage === GroupStorage.SharePoint ? PrincipalType.SharePointUser : PrincipalType.DirectoryGroup, [Validators.required]),
      principalValue: this.fb.control(
        this.groupStorage === GroupStorage.SharePoint ? this.information.principals.users[0].id.toString() : this.information.principals.directoryGroups[0].id.toString(),
        [Validators.required]
      ),
      privilegeLevelID: this.fb.control(this.information.privileges[0].id, [Validators.required]),
      customPrivileges: this.fb.control(this.information.privileges[0].name),
      privilegeLevelName: this.fb.control(null)
    });
    this.formText = {
      filterType: {
        label: "Filter Type",
        labelClass: "col-lg-4",
        inputContainerClass: "col-lg-6",
        popover: this.filterTypePopover,
        options: Object.keys(FilterType)
          .filter((k) => isNaN(Number(k)))
          .map((k) => ({ label: FilterTypeName.get(FilterType[k]), value: FilterType[k] }))
      },
      filterValue: {
        label: "Filter Value",
        labelClass: "col-lg-4",
        inputContainerClass: "col-lg-6",
        popover: this.filterValuePopover,
        options: [this.noneOption, ...this.information.entities.map((e) => ({ label: e.displayName, value: e.logicalName }))],
        errors: {
          required: "The Filter Value field is required"
        }
      },
      principalType: {
        label: "Principal Type",
        labelClass: "col-lg-4",
        inputContainerClass: "col-lg-6",
        popover: this.principalTypePopover,
        options: Object.keys(PrincipalType)
          .filter((k) => isNaN(Number(k)))
          .map((k) => ({ label: PrincipalTypeMap.get(PrincipalType[k]), value: PrincipalType[k] }))
          .slice(this.groupStorage === GroupStorage.SharePoint ? 0 : 2)
      },
      principalValue: {
        label: "Principal Value",
        labelClass: "col-lg-4",
        inputContainerClass: "col-lg-6",
        popover: this.principalValuePopover,
        options:
          // eslint-disable-next-line eqeqeq
          this.groupStorage == GroupStorage.SharePoint
            ? [this.noneOption, ...this.information.principals.users.map((u) => ({ value: u.id, label: u.name }))]
            : [this.noneOption, ...this.information.principals.directoryGroups.map((g) => ({ value: g.id, label: g.name }))],
        errors: {
          required: "The Principal Value field is required"
        }
      },
      privilegeLevelID: {
        label: "Permission Level",
        labelClass: "col-lg-4",
        inputContainerClass: "col-lg-6",
        popover: "The name of a permission level in SharePoint.",
        options: this.information.privileges.map((p) => ({ label: p.name, value: p.id }))
      }
    };

    if (this.entity) {
      // eslint-disable-next-line eqeqeq
      if (this.entity.filterType == FilterType.Entity) this.filterValueOption = this.entity.filterValue;
      else this.filterValueTxt = this.entity.filterValue;

      this.formGroup.patchValue(this.entity);
      this.formGroup.updateValueAndValidity();
    }

    this.formGroup.controls.filterType.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((value) => {
      // eslint-disable-next-line eqeqeq
      if (value != FilterType.Global) {
        this.formGroup.controls.filterValue.setValidators(Validators.required);
        this.formGroup.controls.filterValue.markAsUntouched();
        this.formGroup.controls.filterValue.markAsPristine();
        this.formGroup.controls.filterValue.updateValueAndValidity();

        // eslint-disable-next-line eqeqeq
        this.formGroup.controls.filterValue.setValue(value == FilterType.Entity ? this.filterValueOption : this.filterValueTxt);
      } else {
        this.formGroup.controls.filterValue.clearValidators();
        this.formGroup.controls.filterValue.updateValueAndValidity();

        this.formGroup.controls.filterValue.setValue(null);
      }
    });

    this.formGroup.controls.principalType.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((value) => {
      let comboOptions: SelectBoxOption[];
      // eslint-disable-next-line eqeqeq
      if (value == PrincipalType.SharePointUser) {
        comboOptions = this.information.principals.users.map((u) => ({ value: u.id.toString(), label: u.name }));
      }
      // eslint-disable-next-line eqeqeq
      if (value == PrincipalType.SharePointGroup) {
        comboOptions = this.information.principals.sharePointGroups.map((g) => ({ value: g.id.toString(), label: g.name }));
      }
      // eslint-disable-next-line eqeqeq
      if (value == PrincipalType.DirectoryGroup) {
        comboOptions = this.information.principals.directoryGroups.map((g) => ({ value: this.groupStorage === GroupStorage.SharePoint ? g.id.toString() : g.name, label: g.name }));
      }
      // eslint-disable-next-line eqeqeq
      if (value == PrincipalType.CRMUser) {
        comboOptions = this.information.users.map((u) => ({ value: u.id, label: u.name }));
      }
      // eslint-disable-next-line eqeqeq
      if (value == PrincipalType.CRMTeam) {
        comboOptions = this.information.teams.map((t) => ({ value: t.id, label: t.name }));
      }
      this.formText.principalValue = { ...this.formText.principalValue, options: [this.noneOption, ...comboOptions] };
      this.formGroup.controls.principalValue.setValue(this.entity && this.entity.principalType == value ? this.entity.principalValue : comboOptions[0].value);
    });

    this.formGroup.controls.privilegeLevelID.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((value) => {
      // eslint-disable-next-line eqeqeq
      const privilegeLevel = this.information.privileges.find((p) => p.id == value);
      this.formGroup.controls.privilegeLevelName.setValue(privilegeLevel?.name);
    });
  }

  save() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
    } else {
      this.saveCallback(this.formGroup.getRawValue());
    }
  }
}
